/**
 * MGRS Grid drawing on canvas.
 * Ported from react-leaflet-mgrs-graticule/src/MgrsGraticule.js
 */

import { CoordinateUtils } from './CoordinateUtils';
import { MgrsGridOptions, DEFAULT_MGRS_OPTIONS, RenderBounds, LatLng, PixelPoint } from './types';
import { llToMgrs, llToUtm, utmToLl } from './MgrsCoordinates';
import {
    connectToGzdBoundary,
    drawLabel,
    getAdjustedLatitude,
    getAllVisibleGzds,
    getGzd,
    getLineSlope,
} from './MgrsUtils';

// Indices for GZD boundary coordinates
const SW_INDEX = 0;
const NW_INDEX = 1;
const NE_INDEX = 2;

const LATITUDE_INDEX = 1;
const LONGITUDE_INDEX = 0;

const MGRS_REGEX = /([0-9]+[A-Z])([A-Z]{2})(\d+)/;
const GZD_INDEX = 1;
const HK_INDEX = 2;

/**
 * Draws MGRS grid on a canvas.
 */
export class MgrsGridDrawer {
    private options: MgrsGridOptions;
    private currLatInterval = 8;
    private currLngInterval = 6;
    private mgrsGridInterval: number | null = null;

    constructor(options?: Partial<MgrsGridOptions>) {
        this.options = { ...DEFAULT_MGRS_OPTIONS, ...options };
    }

    /**
     * Draw the complete MGRS grid on the canvas.
     * @param ctx - Canvas 2D rendering context
     * @param coordUtils - Coordinate utilities for the current render
     * @param gridIntervalOverride - Optional grid interval override (100000, 10000, 1000, or 100 meters)
     */
    draw(ctx: CanvasRenderingContext2D, coordUtils: CoordinateUtils, gridIntervalOverride?: number): void {
        const zoom = coordUtils.getZoom();
        const hasOverride = gridIntervalOverride !== undefined;

        // Use override if provided, otherwise determine based on zoom
        if (hasOverride) {
            this.mgrsGridInterval = gridIntervalOverride;
        } else if (zoom > this.options.hundredMMinZoom) {
            this.mgrsGridInterval = 100;
        } else if (zoom > this.options.oneKMinZoom) {
            this.mgrsGridInterval = 1000;
        } else if (zoom > this.options.tenKMinZoom) {
            this.mgrsGridInterval = 10000;
        } else if (zoom > this.options.hundredKMinZoom) {
            this.mgrsGridInterval = 100000;
        } else {
            this.mgrsGridInterval = null;
        }

        // Draw grid first (so GZD lines are on top)
        // Pass hasOverride to bypass zoom checks when user explicitly selected a grid size
        this.drawGrid(ctx, coordUtils, hasOverride);

        // Draw GZD boundaries
        this.drawGzd(ctx, coordUtils);
    }

    /**
     * Draw GZD (Grid Zone Designator) boundaries.
     */
    private drawGzd(ctx: CanvasRenderingContext2D, coordUtils: CoordinateUtils): void {
        const bounds = coordUtils.getBounds();
        const canvasSize = coordUtils.getCanvasSize();

        ctx.lineWidth = this.options.gzdWeight;
        ctx.strokeStyle = this.options.gzdColor;
        ctx.fillStyle = this.options.gzdColor;
        ctx.setLineDash(this.options.gzdDashArray);
        ctx.font = this.options.gzdFont;

        let leftTop = { lat: bounds.north, lng: bounds.west };
        let rightBottom = { lat: bounds.south, lng: bounds.east };

        const pointPerLat = (leftTop.lat - rightBottom.lat) / (canvasSize.height * 0.2);
        const pointPerLon = (rightBottom.lng - leftTop.lng) / (canvasSize.width * 0.2);

        if (isNaN(pointPerLat) || isNaN(pointPerLon)) {
            return;
        }

        // Clamp to valid MGRS range
        if (rightBottom.lat < -80) {
            rightBottom.lat = -80;
        } else {
            rightBottom.lat = parseInt(String(rightBottom.lat - Math.max(pointPerLat, 1)), 10);
        }

        if (leftTop.lat > 84) {
            leftTop.lat = 84;
        } else {
            leftTop.lat = parseInt(String(leftTop.lat + Math.max(pointPerLat, 1)), 10);
        }

        if (leftTop.lng > 0 && rightBottom.lng < 0) {
            rightBottom.lng += 360;
        }
        rightBottom.lng = parseInt(String(rightBottom.lng + Math.max(pointPerLon, 1)), 10);
        leftTop.lng = parseInt(String(leftTop.lng - Math.max(pointPerLon, 1)), 10);

        // Northern hemisphere latitude lines
        for (let i = this.currLatInterval; i <= leftTop.lat; i += this.currLatInterval) {
            if (i >= rightBottom.lat) {
                // Handle 'X' MGRS Zone
                if (i === 80) {
                    i = 84;
                }
                this.drawLatitudeLine(ctx, coordUtils, i, leftTop.lng, rightBottom.lng);
            }
        }

        // Southern hemisphere latitude lines
        for (let i = 0; i >= rightBottom.lat; i -= this.currLatInterval) {
            if (i <= leftTop.lat) {
                this.drawLatitudeLine(ctx, coordUtils, i, leftTop.lng, rightBottom.lng);
            }
        }

        // Longitude lines
        for (let i = -180; i <= rightBottom.lng + 6; i += this.currLngInterval) {
            this.drawLongitudeLine(ctx, coordUtils, i, leftTop.lat, rightBottom.lat);
        }
    }

    /**
     * Draw a latitude line.
     */
    private drawLatitudeLine(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tick: number,
        lngLeft: number,
        lngRight: number
    ): void {
        const leftEnd = coordUtils.latLngToPixel(tick, lngLeft);
        const rightEnd = coordUtils.latLngToPixel(tick, lngRight);

        ctx.beginPath();
        ctx.moveTo(leftEnd.x, leftEnd.y);
        ctx.lineTo(rightEnd.x, rightEnd.y);
        ctx.stroke();
    }

    /**
     * Draw a longitude line with special handling for Norway and Svalbard.
     */
    private drawLongitudeLine(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tick: number,
        latTop: number,
        latBottom: number
    ): void {
        if (latTop >= 84) latTop = 84;
        if (latBottom <= -80) latBottom = -80;

        const canvasTop = coordUtils.latLngToPixel(latTop, tick);
        const canvasBottom = coordUtils.latLngToPixel(latBottom, tick);
        const TOP_OF_W_SERIES_GZD = 72;

        ctx.beginPath();

        // Handle Norway (tick === 6)
        if (tick === 6) {
            this.drawNorwayLongitudeLine(ctx, coordUtils, tick, latTop, latBottom, canvasTop, canvasBottom);
        } else if (tick === 12) {
            this.drawSvalbardLine12(ctx, coordUtils, tick, latTop, latBottom, canvasTop, canvasBottom, TOP_OF_W_SERIES_GZD);
        } else if (tick === 18 || tick === 30) {
            // Don't draw through Svalbard
            if (latTop > TOP_OF_W_SERIES_GZD) {
                const topPoint = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, tick);
                ctx.moveTo(topPoint.x, topPoint.y);
            } else {
                ctx.moveTo(canvasTop.x, canvasTop.y);
            }
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        } else if (tick === 24) {
            this.drawSvalbardLine24(ctx, coordUtils, tick, latTop, latBottom, canvasTop, canvasBottom, TOP_OF_W_SERIES_GZD);
        } else if (tick === 36) {
            this.drawSvalbardLine36(ctx, coordUtils, tick, latTop, latBottom, canvasTop, canvasBottom, TOP_OF_W_SERIES_GZD);
        } else {
            ctx.moveTo(canvasTop.x, canvasTop.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        }
        ctx.stroke();

        this.drawGzdLabels(ctx, coordUtils, tick);
    }

    /**
     * Draw Norway special case longitude line (tick=6).
     */
    private drawNorwayLongitudeLine(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tick: number,
        latTop: number,
        latBottom: number,
        canvasTop: PixelPoint,
        canvasBottom: PixelPoint
    ): void {
        const TOP_OF_V_SERIES_GZD = 64;
        const BOTTOM_OF_V_SERIES_GZD = 56;
        const RIGHT_OF_31_SERIES_GZD = 3;
        const TOP_OF_W_SERIES_GZD = 72;

        const RIGHT_TOP_OF_GZD = coordUtils.latLngToPixel(TOP_OF_V_SERIES_GZD, tick);
        const LEFT_TOP_OF_GZD = coordUtils.latLngToPixel(TOP_OF_V_SERIES_GZD, RIGHT_OF_31_SERIES_GZD);
        const LEFT_BOTTOM_OF_GZD = coordUtils.latLngToPixel(BOTTOM_OF_V_SERIES_GZD, RIGHT_OF_31_SERIES_GZD);
        const RIGHT_BOTTOM_OF_GZD = coordUtils.latLngToPixel(BOTTOM_OF_V_SERIES_GZD, tick);

        if (latTop > TOP_OF_V_SERIES_GZD && latBottom > BOTTOM_OF_V_SERIES_GZD) {
            // Top segment only
            if (latTop > TOP_OF_W_SERIES_GZD) {
                const TOP_LEFT_OF_32_SERIES_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, tick);
                ctx.moveTo(TOP_LEFT_OF_32_SERIES_GZD.x, TOP_LEFT_OF_32_SERIES_GZD.y);
            } else {
                ctx.moveTo(canvasTop.x, canvasTop.y);
            }
            ctx.lineTo(RIGHT_TOP_OF_GZD.x, RIGHT_TOP_OF_GZD.y);
            ctx.moveTo(LEFT_TOP_OF_GZD.x, LEFT_TOP_OF_GZD.y);
            ctx.lineTo(LEFT_TOP_OF_GZD.x, canvasBottom.y);
        } else if (latTop < TOP_OF_V_SERIES_GZD && latBottom < BOTTOM_OF_V_SERIES_GZD) {
            // Bottom segment only
            ctx.moveTo(LEFT_TOP_OF_GZD.x, canvasTop.y);
            ctx.lineTo(LEFT_BOTTOM_OF_GZD.x, LEFT_BOTTOM_OF_GZD.y);
            ctx.moveTo(RIGHT_BOTTOM_OF_GZD.x, RIGHT_BOTTOM_OF_GZD.y);
            ctx.lineTo(RIGHT_BOTTOM_OF_GZD.x, canvasBottom.y);
        } else if (latTop >= TOP_OF_V_SERIES_GZD && latBottom <= BOTTOM_OF_V_SERIES_GZD) {
            // Entire thing
            if (latTop > TOP_OF_W_SERIES_GZD) {
                const TOP_LEFT_OF_32_SERIES_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, tick);
                ctx.moveTo(TOP_LEFT_OF_32_SERIES_GZD.x, TOP_LEFT_OF_32_SERIES_GZD.y);
            } else {
                ctx.moveTo(canvasTop.x, canvasTop.y);
            }
            ctx.lineTo(RIGHT_TOP_OF_GZD.x, RIGHT_TOP_OF_GZD.y);
            ctx.moveTo(LEFT_TOP_OF_GZD.x, LEFT_TOP_OF_GZD.y);
            ctx.lineTo(LEFT_BOTTOM_OF_GZD.x, LEFT_BOTTOM_OF_GZD.y);
            ctx.moveTo(RIGHT_TOP_OF_GZD.x, LEFT_BOTTOM_OF_GZD.y);
            ctx.lineTo(RIGHT_TOP_OF_GZD.x, canvasBottom.y);
        } else if (latTop <= TOP_OF_V_SERIES_GZD && latBottom >= BOTTOM_OF_V_SERIES_GZD) {
            // Modified vertical only
            ctx.moveTo(LEFT_TOP_OF_GZD.x, canvasTop.y);
            ctx.lineTo(LEFT_BOTTOM_OF_GZD.x, canvasBottom.y);
        }
    }

    /**
     * Draw Svalbard special case for tick=12.
     */
    private drawSvalbardLine12(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tick: number,
        latTop: number,
        latBottom: number,
        canvasTop: PixelPoint,
        canvasBottom: PixelPoint,
        TOP_OF_W_SERIES_GZD: number
    ): void {
        if (latTop > TOP_OF_W_SERIES_GZD && latTop <= 84) {
            const TOP_LEFT_OF_33X_GZD = coordUtils.latLngToPixel(latTop, 9);
            ctx.moveTo(TOP_LEFT_OF_33X_GZD.x, TOP_LEFT_OF_33X_GZD.y);

            const BOTTOM_LEFT_OF_33X_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, 9);
            ctx.lineTo(BOTTOM_LEFT_OF_33X_GZD.x, BOTTOM_LEFT_OF_33X_GZD.y);

            const TOP_RIGHT_OF_32W_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, tick);
            ctx.moveTo(TOP_RIGHT_OF_32W_GZD.x, TOP_RIGHT_OF_32W_GZD.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        } else {
            ctx.moveTo(canvasTop.x, canvasTop.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        }
    }

    /**
     * Draw Svalbard special case for tick=24.
     */
    private drawSvalbardLine24(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tick: number,
        latTop: number,
        latBottom: number,
        canvasTop: PixelPoint,
        canvasBottom: PixelPoint,
        TOP_OF_W_SERIES_GZD: number
    ): void {
        if (latTop > TOP_OF_W_SERIES_GZD && latTop <= 84) {
            const TOP_LEFT_OF_35X_GZD = coordUtils.latLngToPixel(latTop, 21);
            ctx.moveTo(TOP_LEFT_OF_35X_GZD.x, TOP_LEFT_OF_35X_GZD.y);

            const BOTTOM_LEFT_OF_35X_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, 21);
            ctx.lineTo(BOTTOM_LEFT_OF_35X_GZD.x, BOTTOM_LEFT_OF_35X_GZD.y);

            const TOP_RIGHT_OF_34W_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, tick);
            ctx.moveTo(TOP_RIGHT_OF_34W_GZD.x, TOP_RIGHT_OF_34W_GZD.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        } else {
            ctx.moveTo(canvasTop.x, canvasTop.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        }
    }

    /**
     * Draw Svalbard special case for tick=36.
     */
    private drawSvalbardLine36(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        tick: number,
        latTop: number,
        latBottom: number,
        canvasTop: PixelPoint,
        canvasBottom: PixelPoint,
        TOP_OF_W_SERIES_GZD: number
    ): void {
        if (latTop > TOP_OF_W_SERIES_GZD && latTop <= 84) {
            const TOP_LEFT_OF_37X_GZD = coordUtils.latLngToPixel(latTop, 33);
            ctx.moveTo(TOP_LEFT_OF_37X_GZD.x, TOP_LEFT_OF_37X_GZD.y);

            const BOTTOM_LEFT_OF_37X_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, 33);
            ctx.lineTo(BOTTOM_LEFT_OF_37X_GZD.x, BOTTOM_LEFT_OF_37X_GZD.y);

            const TOP_RIGHT_OF_36W_GZD = coordUtils.latLngToPixel(TOP_OF_W_SERIES_GZD, tick);
            ctx.moveTo(TOP_RIGHT_OF_36W_GZD.x, TOP_RIGHT_OF_36W_GZD.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        } else {
            ctx.moveTo(canvasTop.x, canvasTop.y);
            ctx.lineTo(canvasBottom.x, canvasBottom.y);
        }
    }

    /**
     * Draw GZD labels.
     */
    private drawGzdLabels(ctx: CanvasRenderingContext2D, coordUtils: CoordinateUtils, longitude: number): void {
        for (let labelLatitude = -76; labelLatitude < 84; labelLatitude += 8) {
            let labelLongitude: number;

            if (labelLatitude === 60) {
                if (longitude === 0) {
                    labelLongitude = 1.5;
                } else if (longitude === 6) {
                    labelLongitude = 7.5;
                } else {
                    labelLongitude = longitude + 3;
                }
            } else if (labelLatitude === 76) {
                if (longitude === 0) {
                    labelLongitude = 4.5;
                } else if (longitude === 12) {
                    labelLongitude = 15;
                } else if (longitude === 24) {
                    labelLongitude = 27;
                } else if (longitude === 36) {
                    labelLongitude = 37.5;
                } else {
                    labelLongitude = longitude + 3;
                }
            } else {
                labelLongitude = longitude + 3;
            }

            let gzdLabel: string;
            try {
                const mgrs = llToMgrs([labelLongitude, labelLatitude], 1);
                const match = mgrs.match(MGRS_REGEX);
                if (!match) continue;
                gzdLabel = match[GZD_INDEX];
            } catch (error) {
                continue;
            }

            // Don't display duplicates
            if (
                !(gzdLabel === '33X' && longitude === 6) &&
                !(gzdLabel === '35X' && longitude === 18) &&
                !(gzdLabel === '37X' && longitude === 30)
            ) {
                const labelXy = coordUtils.latLngToPixel(labelLatitude, labelLongitude);
                drawLabel(ctx, gzdLabel, this.options.gzdFontColor, this.options.gzdColor, labelXy);
            }
        }
    }

    /**
     * Draw the detailed MGRS grid (100km, 10km, 1km, 100m).
     * @param ctx - Canvas 2D rendering context
     * @param coordUtils - Coordinate utilities
     * @param forceRender - If true, bypass zoom level checks (used when user explicitly selects grid size)
     */
    private drawGrid(ctx: CanvasRenderingContext2D, coordUtils: CoordinateUtils, forceRender: boolean = false): void {
        // Only check zoom level if not forcing render
        if (!forceRender && coordUtils.getZoom() < this.options.hundredKMinZoom) {
            return;
        }

        if (this.mgrsGridInterval === null) {
            return;
        }

        ctx.lineWidth = this.options.gridWeight + 0.75;
        ctx.strokeStyle = this.options.gridFontColor;
        ctx.fillStyle = this.options.gridColor;
        ctx.setLineDash(this.options.gzdDashArray);
        ctx.font = this.options.gridFont;

        const visibleGzds = this.getVisibleGzds(coordUtils);
        if (!visibleGzds) return;

        const bounds = coordUtils.getBounds();

        visibleGzds.forEach((gzd) => {
            try {
                this.drawGzdGrid(ctx, coordUtils, gzd, bounds);
            } catch (e) {
                // Skip invalid GZDs
            }
        });
    }

    /**
     * Get visible GZDs for the current bounds.
     */
    private getVisibleGzds(coordUtils: CoordinateUtils): string[] | null {
        const bounds = coordUtils.getBounds();

        try {
            const nwBoundMgrs = llToMgrs([bounds.west, bounds.north], 1);
            const neBoundMgrs = llToMgrs([bounds.east, bounds.north], 1);
            const seBoundMgrs = llToMgrs([bounds.east, bounds.south], 1);
            const swBoundMgrs = llToMgrs([bounds.west, bounds.south], 1);

            const nwMatch = nwBoundMgrs.match(MGRS_REGEX);
            const neMatch = neBoundMgrs.match(MGRS_REGEX);
            const seMatch = seBoundMgrs.match(MGRS_REGEX);
            const swMatch = swBoundMgrs.match(MGRS_REGEX);

            if (!nwMatch || !neMatch || !seMatch || !swMatch) return null;

            return getAllVisibleGzds(
                nwMatch[GZD_INDEX],
                neMatch[GZD_INDEX],
                seMatch[GZD_INDEX],
                swMatch[GZD_INDEX]
            );
        } catch (e) {
            return null;
        }
    }

    /**
     * Draw the grid for a single GZD.
     */
    private drawGzdGrid(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        gzd: string,
        mapBounds: RenderBounds
    ): void {
        const gzdObject = getGzd(gzd);

        const gzdWestBoundary = gzdObject[NW_INDEX][LONGITUDE_INDEX];
        const gzdEastBoundary = gzdObject[NE_INDEX][LONGITUDE_INDEX];
        const gzdNorthBoundary = gzdObject[NW_INDEX][LATITUDE_INDEX];
        const gzdSouthBoundary = gzdObject[SW_INDEX][LATITUDE_INDEX];

        // Calculate effective boundaries
        const effectiveWestBoundary =
            gzdWestBoundary < mapBounds.west && this.mgrsGridInterval !== 100000
                ? mapBounds.west
                : gzdWestBoundary;
        const effectiveEastBoundary =
            gzdEastBoundary > mapBounds.east && this.mgrsGridInterval !== 100000
                ? mapBounds.east
                : gzdEastBoundary;
        const effectiveNorthBoundary = gzdNorthBoundary > mapBounds.north ? mapBounds.north : gzdNorthBoundary;
        const effectiveSouthBoundary = gzdSouthBoundary < mapBounds.south ? mapBounds.south : gzdSouthBoundary;

        // Buffer to ensure we're inside the GZD
        const buffer = 0.00001;
        const swCornerUtm = llToUtm(effectiveSouthBoundary + buffer, effectiveWestBoundary + buffer);
        const seCornerUtm = llToUtm(effectiveSouthBoundary + buffer, effectiveEastBoundary - buffer);
        const nwCornerUtm = llToUtm(effectiveNorthBoundary - buffer, effectiveWestBoundary + buffer);
        const neCornerUtm = llToUtm(effectiveNorthBoundary - buffer, effectiveEastBoundary - buffer);

        // Calculate center latitude to determine which hemisphere we're in
        const centerLat = (effectiveNorthBoundary + effectiveSouthBoundary) / 2;
        let startingEasting = centerLat >= 0 ? swCornerUtm.easting : nwCornerUtm.easting;
        let finalEasting = centerLat >= 0 ? seCornerUtm.easting : neCornerUtm.easting;

        let startingNorthing = Math.min(swCornerUtm.northing, seCornerUtm.northing);
        let finalNorthing = Math.max(nwCornerUtm.northing, neCornerUtm.northing);

        // Round to grid interval
        startingEasting = Math.floor(startingEasting / this.mgrsGridInterval!) * this.mgrsGridInterval!;
        finalEasting = Math.ceil(finalEasting / this.mgrsGridInterval!) * this.mgrsGridInterval!;
        startingNorthing = Math.floor(startingNorthing / this.mgrsGridInterval!) * this.mgrsGridInterval!;
        finalNorthing = Math.ceil(finalNorthing / this.mgrsGridInterval!) * this.mgrsGridInterval!;

        // Build arrays
        const eastingArray: number[] = [];
        for (let i = startingEasting; i <= finalEasting; i += this.mgrsGridInterval!) {
            eastingArray.push(i);
        }

        const northingArray: number[] = [];
        for (let i = startingNorthing; i <= finalNorthing; i += this.mgrsGridInterval!) {
            northingArray.push(i);
        }

        const zoneLetter = nwCornerUtm.zoneLetter;
        const zoneNumber = nwCornerUtm.zoneNumber;

        // Draw easting lines (lines of constant easting)
        this.drawEastingLines(ctx, coordUtils, eastingArray, northingArray, zoneNumber, zoneLetter, gzdWestBoundary, gzdEastBoundary, gzdNorthBoundary, gzdSouthBoundary);

        // Draw northing lines (lines of constant northing)
        this.drawNorthingLines(ctx, coordUtils, eastingArray, northingArray, zoneNumber, zoneLetter, gzdNorthBoundary, gzdSouthBoundary, effectiveWestBoundary, effectiveEastBoundary);

        // Draw labels
        this.drawGridLabels(ctx, coordUtils, eastingArray, northingArray, zoneNumber, zoneLetter, effectiveNorthBoundary, effectiveSouthBoundary, effectiveWestBoundary, effectiveEastBoundary);
    }

    /**
     * Draw easting lines.
     */
    private drawEastingLines(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        eastingArray: number[],
        northingArray: number[],
        zoneNumber: number,
        zoneLetter: string,
        gzdWestBoundary: number,
        gzdEastBoundary: number,
        gzdNorthBoundary: number,
        gzdSouthBoundary: number
    ): void {
        eastingArray.forEach((eastingElem) => {
            let initialPlacementCompleted = false;
            ctx.beginPath();

            try {
                northingArray.forEach((northingElem, northingIndex, northArr) => {
                    let gridIntersectionLl = utmToLl(eastingElem, northingElem, zoneNumber, zoneLetter);

                    if (gridIntersectionLl.lng > gzdEastBoundary || gridIntersectionLl.lng < gzdWestBoundary) {
                        return;
                    }

                    // Truncate at GZD boundaries
                    if (gridIntersectionLl.lat < gzdSouthBoundary && northArr[northingIndex + 1]) {
                        const nextIntersectionLl = utmToLl(eastingElem, northArr[northingIndex + 1], zoneNumber, zoneLetter);
                        gridIntersectionLl = connectToGzdBoundary(gridIntersectionLl, nextIntersectionLl, 'North');
                    } else if (gridIntersectionLl.lat > gzdNorthBoundary && northArr[northingIndex - 1]) {
                        const previousIntersectionLl = utmToLl(eastingElem, northArr[northingIndex - 1], zoneNumber, zoneLetter);
                        gridIntersectionLl = connectToGzdBoundary(gridIntersectionLl, previousIntersectionLl, 'South');
                    }

                    if (Number.isFinite(gridIntersectionLl.lat) && Number.isFinite(gridIntersectionLl.lng)) {
                        const gridIntersectionXy = coordUtils.latLngToPixel(gridIntersectionLl.lat, gridIntersectionLl.lng);
                        if (!initialPlacementCompleted) {
                            ctx.moveTo(gridIntersectionXy.x, gridIntersectionXy.y);
                            initialPlacementCompleted = true;
                        } else {
                            ctx.lineTo(gridIntersectionXy.x, gridIntersectionXy.y);
                        }
                    }
                });

                const notHkLine = eastingElem % 100000 !== 0;
                this.drawLine(ctx, notHkLine);
            } catch (e) {
                // Skip problematic lines
            }
        });
    }

    /**
     * Draw northing lines.
     */
    private drawNorthingLines(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        eastingArray: number[],
        northingArray: number[],
        zoneNumber: number,
        zoneLetter: string,
        gzdNorthBoundary: number,
        gzdSouthBoundary: number,
        effectiveWestBoundary: number,
        effectiveEastBoundary: number
    ): void {
        northingArray.forEach((northingElem) => {
            let beginPathCalled = false;

            eastingArray.forEach((eastingElem, eastingIndex, eastArr) => {
                let gridIntersectionLl = utmToLl(eastingElem, northingElem, zoneNumber, zoneLetter);

                if (gridIntersectionLl.lat > gzdNorthBoundary || gridIntersectionLl.lat < gzdSouthBoundary) {
                    if (!beginPathCalled) {
                        ctx.beginPath();
                        beginPathCalled = true;
                    }
                    return;
                }

                let gridIntersectionXy = coordUtils.latLngToPixel(gridIntersectionLl.lat, gridIntersectionLl.lng);

                if (!beginPathCalled) {
                    // Truncate at western boundary
                    if (gridIntersectionLl.lng < effectiveWestBoundary && eastArr[eastingIndex + 1]) {
                        const nextGridIntersectionLl = utmToLl(eastArr[eastingIndex + 1], northingElem, zoneNumber, zoneLetter);
                        if (nextGridIntersectionLl.lng < effectiveWestBoundary) {
                            return;
                        }
                        const slope = getLineSlope(gridIntersectionLl, nextGridIntersectionLl);
                        try {
                            gridIntersectionLl = {
                                lat: getAdjustedLatitude(slope, effectiveWestBoundary, gridIntersectionLl),
                                lng: effectiveWestBoundary,
                            };
                            gridIntersectionXy = coordUtils.latLngToPixel(gridIntersectionLl.lat, gridIntersectionLl.lng);
                        } catch (e) {
                            return;
                        }
                    }
                    ctx.beginPath();
                    beginPathCalled = true;
                    ctx.moveTo(gridIntersectionXy.x, gridIntersectionXy.y);
                } else {
                    // Truncate at eastern boundary
                    if (gridIntersectionLl.lng > effectiveEastBoundary && eastArr[eastingIndex - 1]) {
                        const previousGridIntersectionLl = utmToLl(eastArr[eastingIndex - 1], northingElem, zoneNumber, zoneLetter);
                        const slope = getLineSlope(gridIntersectionLl, previousGridIntersectionLl);
                        try {
                            gridIntersectionLl = {
                                lat: getAdjustedLatitude(slope, effectiveEastBoundary, gridIntersectionLl),
                                lng: effectiveEastBoundary,
                            };
                            gridIntersectionXy = coordUtils.latLngToPixel(gridIntersectionLl.lat, gridIntersectionLl.lng);
                        } catch (e) {
                            return;
                        }
                    }
                    ctx.lineTo(gridIntersectionXy.x, gridIntersectionXy.y);
                }
            });

            const notHkLine = northingElem % 100000 !== 0;
            this.drawLine(ctx, notHkLine);
        });
    }

    /**
     * Draw grid labels.
     */
    private drawGridLabels(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        eastingArray: number[],
        northingArray: number[],
        zoneNumber: number,
        zoneLetter: string,
        effectiveNorthBoundary: number,
        effectiveSouthBoundary: number,
        effectiveWestBoundary: number,
        effectiveEastBoundary: number
    ): void {
        if (this.mgrsGridInterval === 100000) {
            this.drawHkLabels(ctx, coordUtils, eastingArray, northingArray, zoneNumber, zoneLetter, effectiveNorthBoundary, effectiveSouthBoundary, effectiveWestBoundary, effectiveEastBoundary);
        } else {
            this.drawCoordinateLabels(ctx, coordUtils, eastingArray, northingArray, zoneNumber, zoneLetter, effectiveSouthBoundary, effectiveEastBoundary);
        }
    }

    /**
     * Draw 100km square labels.
     */
    private drawHkLabels(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        eastingArray: number[],
        northingArray: number[],
        zoneNumber: number,
        zoneLetter: string,
        effectiveNorthBoundary: number,
        effectiveSouthBoundary: number,
        effectiveWestBoundary: number,
        effectiveEastBoundary: number
    ): void {
        eastingArray.forEach((eastingElem, eastingIndex, ea) => {
            northingArray.forEach((northingElem, northingIndex, na) => {
                let currentLl = utmToLl(eastingElem, northingElem, zoneNumber, zoneLetter);
                let adjacentLlNorthing: LatLng | null = null;
                let adjacentLlEasting: LatLng | null = null;

                if (ea[eastingIndex + 1]) {
                    adjacentLlEasting = utmToLl(ea[eastingIndex + 1], northingElem, zoneNumber, zoneLetter);
                    if (adjacentLlEasting.lng > effectiveEastBoundary) {
                        const slope = getLineSlope(currentLl, adjacentLlEasting);
                        adjacentLlEasting = {
                            lat: getAdjustedLatitude(slope, effectiveEastBoundary, adjacentLlEasting),
                            lng: effectiveEastBoundary,
                        };
                    }
                } else {
                    return;
                }

                if (na[northingIndex + 1]) {
                    if (eastingIndex === 0) {
                        adjacentLlNorthing = utmToLl(ea[eastingIndex + 1], na[northingIndex + 1], zoneNumber, zoneLetter);
                    } else {
                        adjacentLlNorthing = utmToLl(eastingElem, na[northingIndex + 1], zoneNumber, zoneLetter);
                    }
                } else {
                    return;
                }

                // Boundary check
                if (currentLl.lng < effectiveWestBoundary) {
                    const slope = getLineSlope(currentLl, adjacentLlEasting);
                    currentLl = {
                        lat: getAdjustedLatitude(slope, effectiveWestBoundary, currentLl),
                        lng: effectiveWestBoundary,
                    };
                } else if (currentLl.lng > effectiveEastBoundary) {
                    return;
                }

                const labelLl: LatLng = {
                    lat: (currentLl.lat + adjacentLlNorthing.lat) / 2,
                    lng: (currentLl.lng + adjacentLlEasting.lng) / 2,
                };

                try {
                    if (
                        labelLl.lat >= effectiveSouthBoundary &&
                        labelLl.lat <= effectiveNorthBoundary &&
                        labelLl.lng >= effectiveWestBoundary &&
                        labelLl.lng <= effectiveEastBoundary
                    ) {
                        const mgrs = llToMgrs([labelLl.lng, labelLl.lat]);
                        const match = mgrs.match(MGRS_REGEX);
                        if (!match) return;
                        const labelText = match[HK_INDEX];

                        const currentXy = coordUtils.latLngToPixel(currentLl.lat, currentLl.lng);
                        const adjacentXy = coordUtils.latLngToPixel(adjacentLlEasting.lat, adjacentLlEasting.lng);
                        const distance = Math.sqrt(
                            Math.pow(currentXy.x - adjacentXy.x, 2) + Math.pow(currentXy.y - adjacentXy.y, 2)
                        );

                        if (distance < ctx.measureText(labelText).width * 2) {
                            return;
                        }

                        const labelXy = coordUtils.latLngToPixel(labelLl.lat, labelLl.lng);
                        drawLabel(ctx, labelText, this.options.gridFontColor, this.options.hkColor, labelXy);
                    }
                } catch (e) {
                    return;
                }
            });
        });
    }

    /**
     * Draw coordinate labels for finer grids.
     */
    private drawCoordinateLabels(
        ctx: CanvasRenderingContext2D,
        coordUtils: CoordinateUtils,
        eastingArray: number[],
        northingArray: number[],
        zoneNumber: number,
        zoneLetter: string,
        effectiveSouthBoundary: number,
        effectiveEastBoundary: number
    ): void {
        // Easting labels at bottom
        eastingArray.forEach((eastingElem, eastingIndex, ea) => {
            if (eastingIndex === 0 || eastingIndex === ea.length - 1) return;

            try {
                const labelLl = utmToLl(eastingElem, northingArray[1], zoneNumber, zoneLetter);
                const labelXy = coordUtils.latLngToPixel(effectiveSouthBoundary, labelLl.lng);
                const labelText = this.getLabelText(eastingElem);

                drawLabel(ctx, labelText, this.options.gridFontColor, this.options.gridColor, {
                    x: labelXy.x,
                    y: labelXy.y - 15,
                });
            } catch (e) {
                return;
            }
        });

        // Northing labels at right
        northingArray.forEach((northingElem) => {
            try {
                const labelLl = utmToLl(eastingArray[eastingArray.length - 1], northingElem, zoneNumber, zoneLetter);
                const labelXy = coordUtils.latLngToPixel(labelLl.lat, effectiveEastBoundary);
                const labelText = this.getLabelText(northingElem);

                drawLabel(ctx, labelText, this.options.gridFontColor, this.options.gridColor, {
                    x: labelXy.x - 15,
                    y: labelXy.y,
                });
            } catch (e) {
                return;
            }
        });
    }

    /**
     * Get label text for a coordinate value.
     */
    private getLabelText(element: number): string {
        let label =
            this.mgrsGridInterval === 10000 || this.mgrsGridInterval === 1000
                ? ((element % 100000) / 1000).toString()
                : ((element % 100000) / 100).toString();

        if (this.mgrsGridInterval === 100) {
            label = label.padStart(3, '0');
        }

        return label;
    }

    /**
     * Draw a line with appropriate styling.
     */
    private drawLine(ctx: CanvasRenderingContext2D, notHkLine: boolean): void {
        if (notHkLine) {
            ctx.setLineDash([]);
            ctx.lineWidth = this.options.gridWeight + 1;
            ctx.strokeStyle = this.options.gridFontColor;
            ctx.stroke();
            ctx.lineWidth = this.options.gridWeight;
            ctx.strokeStyle = this.options.gridColor;
            ctx.stroke();
        } else {
            ctx.lineWidth = this.options.gridWeight;
            ctx.strokeStyle = this.options.hkColor;
            ctx.setLineDash(this.options.hkDashArray);
            ctx.stroke();
        }
    }
}
